import { verifyRoutePermission } from "@/router/guard";
import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";

// Rota padrão do processo é a Requisição; sem permissão para ela, cai na primeira aba que o usuário pode ver.
const redirectIfRequisitionHidden = (to: RouteLocationNormalized) => {
    const authStore = useAuthStore();
    if (authStore.hasPermission('ats.process.tab.requisition.view')) return;

    const fallbacks = [
        ['screening', 'atsProcessesScreening'],
        ['hunting', 'atsProcessesHunting'],
        ['selection', 'atsProcessesSelection'],
        ['disqualified', 'atsProcessesDisqualified'],
        ['reports', 'atsProcessesFormReports'],
    ];
    const fallback = fallbacks.find(([tab]) => authStore.hasPermission(`ats.process.tab.${tab}.view`));
    if (fallback) return { name: fallback[1], params: to.params, replace: true };
};

export const ATSRoutes: RouteRecordRaw[] = [
    {
        path: '',
        name: 'atsMain',
        component: () => import('../../components/MenuScreen.vue')
    },
        {
        path: '',
        name: 'atsProcesses',
        children: [
            {
                path: 'processes',
                name: 'atsProcessesList',
                component: () => import('./processes/ProcessList.vue')
            },
            {
                path: 'processes/:id',
                name: 'atsProcessesView',
                component: () => import('./processes/ProcessView/Index.vue'),
                children: [
                    {
                        path: '',
                        name: 'atsProcessesRequisition',
                        component: () => import('./processes/ProcessView/Requisition.vue'),
                        beforeEnter: redirectIfRequisitionHidden
                    },
                    {
                        path: 'config',
                        name: 'atsProcessesConfig',
                        component: () => import('./processes/ProcessView/Requisition.vue')
                    },
                    {
                        path: 'hunting',
                        name: 'atsProcessesHunting',
                        component: () => import('./processes/ProcessView/Hunting.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.hunting.view"] }
                    },
                    {
                        path: 'screening',
                        name: 'atsProcessesScreening',
                        component: () => import('./processes/ProcessView/Screening.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.screening.view"] }
                    },
                    {
                        path: 'selection',
                        name: 'atsProcessesSelection',
                        component: () => import('./processes/ProcessView/Selection.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.selection.view"] }
                    },
                    {
                        path: 'disqualified',
                        name: 'atsProcessesDisqualified',
                        component: () => import('./processes/ProcessView/Disqualified.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.disqualified.view"] }
                    },
                    {
                        path: 'form-reports',
                        name: 'atsProcessesFormReports',
                        component: () => import('./processes/ProcessView/FormReports/Index.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.reports.view"] }
                    },
                    {
                        path: 'billing',
                        name: 'atsProcessesBilling',
                        component: () => import('./processes/ProcessView/Billing.vue'),
                        beforeEnter: verifyRoutePermission,
                        meta: { permission: ["ats.process.access", "ats.process.tab.billing.view"] },
                    }
                ]
            }
        ],
        beforeEnter: verifyRoutePermission,
        meta: { permission: "ats.process.access" },
    },
    {
        path: '',
        name: 'atsCandidates',
        children: [
            {
                path: 'candidates',
                name: 'atsCandidatesList',
                component: () => import('./candidates/CandidatesList.vue')
            },
            {
                path: 'candidates/:id',
                name: 'atsCandidatesView',
                component: () => import('./candidates/CandidatesView/Index.vue')
            }
        ],
        beforeEnter: verifyRoutePermission,
        meta: { permission: "ats.candidate.access" },
    },
    {
        path: '',
        name: 'atsClients',
        children: [
            {
                path: 'clients', name: 'atsClientsList',
                component: () => import('./clients/ClientsList.vue')
            },
            {
                path: 'clients/:id', name: 'atsClientsView',
                component: () => import('./clients/ClientsView.vue')
            }
        ],
        beforeEnter: verifyRoutePermission,
        meta: { permission: "ats.client.access" },
    },
    {
        path: 'models',
        name: 'atsModels',
        children: [
            {
                path: '',
                name: 'atsModelsMain',
                component: () => import('../../components/MenuScreen.vue')
            },
            {
                path: 'opinions', name: 'atsModelsOpinionList',
                component: () => import('./models/opinion/OpinionList.vue')
            },
            {
                path: 'opinions/:id', name: 'atsModelsOpinionEdit',
                component: () => import('./models/opinion/OpinionEdit.vue')
            },
            {
                path: 'mails', name: 'atsModelsMailList',
                component: () => import('./models/mail/MailList.vue')
            },
            {
                path: 'mails/:id', name: 'atsModelsMailEdit',
                component: () => import('./models/mail/MailEdit.vue')
            },
            {
                path: 'processes', name: 'atsModelsProcessList',
                component: () => import('./models/process/ProcessModelList.vue')
            },
            {
                path: 'processes/:id', name: 'atsModelsProcessEdit',
                component: () => import('./models/process/ProcessModelEdit.vue')
            }
        ],
        beforeEnter: verifyRoutePermission,
        meta: { permission: "ats.model.access" },
    },
    {
        path: 'forms',
        name: 'atsForms',
        children: [
            {
                path: '',
                name: 'atsFormsMain',
                component: () => import('../../components/MenuScreen.vue')
            },
            {
                path: 'simple', name: 'atsFormsSimpleList',
                component: () => import('./forms/simple/SimpleList.vue')
            },
            {
                path: 'simple/:id', name: 'atsFormsSimpleEdit',
                component: () => import('./forms/simple/SimpleEdit.vue')
            },
            {
                path: 'technical', name: 'atsFormsTechnicalList',
                component: () => import('./forms/technical/TechnicalList.vue')
            },
            {
                path: 'technical/:id', name: 'atsFormsTechnicalEdit',
                component: () => import('./forms/technical/TechnicalEdit.vue')
            },
            {
                path: 'phase', name: 'atsFormsPhaseList',
                component: () => import('./forms/phase/PhaseList.vue')
            },
            {
                path: 'phase/:id', name: 'atsFormsPhaseEdit',
                component: () => import('./forms/phase/PhaseEdit.vue')
            },
            {
                path: 'candidate', name: 'atsFormsCandidateList',
                component: () => import('./forms/candidate/CandidateList.vue')
            },
            {
                path: 'candidate/:id', name: 'atsFormsCandidateEdit',
                component: () => import('./forms/candidate/CandidateEdit.vue')
            }
        ],
        beforeEnter: verifyRoutePermission,
        meta: { permission: "ats.model.access" },
    }
]